/**
 * oelContext.js — helpers to compute the build-time OEL context that
 * OelEditor needs.
 *
 * Shape (single source of truth):
 *   {
 *     inputs:        [{ name, type, required, description }],
 *     previousSteps: [{ id, kind, output_type, ...rawStepFields }],
 *     definitions:   [{ id, kind, output_type }],
 *     flows:         [{ id, output_type? }],
 *     loopVars:      [{ name, source }],
 *     subflowAvailable: bool  — true inside flows[] (runtime exposes @subflow.*)
 *     subflowParams: [{ name, type?, description? }]
 *     inFlowScope: bool — true inside flow editor; flow IDs are not valid @ roots
 *     flowCatalog: [{ id }] — opcode flow ids (for validation hints when inFlowScope)
 *   }
 *
 * Per UF-2 / user instruction (2026-04-27): the picker exposes ONLY
 * step outputs of *previous* steps so users cannot create forward refs.
 */

/**
 * Build the OEL context for the step at `currentStepIdx` inside an opcode.
 * Pass null/undefined for currentStepIdx to expose ALL steps (used for
 * top-level fields like typed_outputs.oel_expression).
 */
/**
 * Collect `with` parameter names from Subflow steps that call `flowId`.
 */
export function collectSubflowWithKeys(flowId, { steps = [], flows = [] } = {}) {
    if (!flowId) return [];
    const names = new Set();
    const scan = (step) => {
        if (!step || step.kind !== 'subflow') return;
        if (step.subflow?.ref !== flowId) return;
        const withObj = step.with;
        if (withObj && typeof withObj === 'object' && !Array.isArray(withObj)) {
            Object.keys(withObj).forEach((k) => {
                if (k?.trim()) names.add(k.trim());
            });
        }
    };
    (steps || []).forEach(scan);
    (flows || []).forEach((f) => (f.steps || []).forEach(scan));
    return [...names].sort();
}

export function buildOelContext({
    inputs = [],
    steps = [],
    definitions = [],
    flows = [],
    currentStepIdx = null,
    loopVars = [],
    apiSchemaRegistry = null,
    subflowAvailable = false,
    subflowParams = [],
    inFlowScope = false,
    // Parent-procedure steps that are readable from inside a flow. The engine
    // exposes them via a ChainMap fallback (parent steps behind flow-local ones),
    // so a flow invoked by choose/subflow can reference @<parent_step_id>. Passed
    // only for flow contexts; empty for the main procedure. Not sliced by
    // currentStepIdx — every parent step ran before the flow.
    parentSteps = []
} = {}) {
    const localSteps = (currentStepIdx == null
        ? steps
        : steps.slice(0, currentStepIdx)
    )
        .filter((s) => s && s.id)
        .map((s) => ({
            id: s.id,
            kind: s.kind,
            output_type: s.output_type,
            // include the raw step so output-shape lookup can read kind-specific
            // fields (e.g. choose.cases for branching paths)
            raw: s
        }));

    const parentStepsRich = (parentSteps || [])
        .filter((s) => s && s.id)
        // don't shadow a flow-local step of the same id (engine prefers local)
        .filter((s) => !localSteps.some((l) => l.id === s.id))
        .map((s) => ({
            id: s.id,
            kind: s.kind,
            output_type: s.output_type,
            raw: s
        }));

    // Merge so validation, the @ menu, the reference panel, and output-path
    // autocomplete all treat parent steps as valid, pickable references.
    const previousSteps = [...localSteps, ...parentStepsRich];

    const typedInputs = (inputs || []).filter((i) => i && i.name).map((i) => ({
        name: i.name,
        type: i.type || 'string',
        required: i.required !== false,
        description: i.description || ''
    }));

    const flowList = (flows || []).filter((f) => f && f.id).map((f) => ({
        id: f.id,
        output_type: f.output_type
    }));

    return {
        // Legacy back-compat fields (V2 OELCodeEditor reads these as plain
        // string arrays) — keep them so the legacy editor keeps working.
        inputs: typedInputs.map((i) => i.name),
        previousStepIds: previousSteps.map((s) => s.id),
        allStepIds: (steps || []).filter((s) => s && s.id).map((s) => s.id),
        loopVariables: (loopVars || []).map((lv) => lv.name).filter(Boolean),

        // New richer shape (consumed by OelEditor + OelRefMenu).
        typedInputs,
        previousSteps,
        definitions: (definitions || []).filter((d) => d && d.id).map((d) => ({
            id: d.id,
            kind: d.kind,
            output_type: d.output_type
        })),
        flows: inFlowScope ? [] : flowList,
        flowCatalog: flowList,
        loopVars,
        apiSchemaRegistry: apiSchemaRegistry || {},
        subflowAvailable: Boolean(subflowAvailable),
        subflowParams: (subflowParams || []).filter((p) => p?.name).map((p) => ({
            name: p.name,
            type: p.type || 'any',
            description: p.description || ''
        })),
        inFlowScope: Boolean(inFlowScope)
    };
}

/**
 * Backward-compat shim: the existing oelContext shape used by the
 * OpcodeBuilder + InlineOpcodeEditor was `{inputs:[name], previousStepIds:[id]}`.
 * Detect that shape and upgrade it on the fly so legacy callers still work
 * as we sweep the codebase.
 */
export function normalizeOelContext(legacyOrNew, allSteps = []) {
    if (!legacyOrNew) return buildOelContext({});
    // Already in new shape?
    if (Array.isArray(legacyOrNew.previousSteps)) return legacyOrNew;
    // Legacy: convert
    const inputNames = legacyOrNew.inputs || [];
    const prevIds = legacyOrNew.previousStepIds || [];
    const stepsById = new Map(allSteps.map((s) => [s?.id, s]));
    const typedInputs = inputNames.map((name) => (typeof name === 'string'
        ? {
            name,
            type: 'any',
            required: true,
            description: ''
        }
        : name
    ));
    const previousSteps = prevIds.map((id) => {
        const s = stepsById.get(id);
        return s
            ? {
                id: s.id,
                kind: s.kind,
                output_type: s.output_type,
                raw: s
            }
            : {
                id,
                kind: 'unknown'
            };
    });
    return {
        inputs: typedInputs.map((i) => i.name),
        previousStepIds: prevIds,
        allStepIds: legacyOrNew.allStepIds || prevIds,
        loopVariables: (legacyOrNew.loopVariables || []),
        typedInputs,
        previousSteps,
        definitions: legacyOrNew.definitions || [],
        flows: legacyOrNew.flows || [],
        loopVars: legacyOrNew.loopVars || []
    };
}
